import React, { useEffect, useMemo, useRef } from 'react';
import Accordion from '../../components/Accordion/Accordion';
import Input from '../../components/Input/Input';
import Quote from '../../components/Quote/Quote';
import Loader from '../../components/Loader/Loader';
import { AccountInfoSP } from '../PageLayout/PageLayout';
import RevisionMessages from '../Quotes/RevisionMessages';
import { useSelector, useDispatch } from 'react-redux';
import {
    /* SELECTORS */
    selectCustomers,
    selectCustomerInformation,
    selectActivationDuration,
    selectGeneralDetails,
    selectProductInformation,
    selectDiscounts,
    selectProductDescriptions,
    selectMachinesAndTools,
    selectCompleteQuote,
    selectMachineEdit,
    selectMachines,
    selectASMs,
    selectMarkets,
    selectIsQuoteRevision,
    selectQuotePrepared,
    selectLoadingStatus,
    selectUniqueQuoteNumber,
    /* DISPATCH ACTIONS */
    setCustomer,
    setActivationDate,
    setExpirationDate,
    setOpportunityNumber,
    setEstimatedTotalValue,
    setQuoteSubject,
    setMarket,
    setASM,
    setAttn,
    setStandardItem,
    addStandardItem,
    setMiscellaneousItem,
    addMiscellaneousItem,
    setMachineRental,
    setMachineRentalEdit,
    setFlags,
    getProductDescriptions,
    getMachines,
    addMachineRental,
    getAllASMs,
    getMarkets,
    prepareQuote,
    setQuotePrepared,
    putQuote,
    monthDayYearToIso,
    identifyAndCreateFlags,
    setNotifications,
    setLoadingStatus,
    clearQuote,
    setQuoteNumber,
    checkQuoteNumber,
    clearMachineItem,
    setAdditionalNotes,
    generateMinOrMaxDate,
    isoToMonthDayYear,
    createISOdateString,
    revisionReset,
    formatCustomerNumber
} from '../../store/quotesAndNotificationsSlice';
import { 
    setSidePanelTitle,
    setSidePanelOptions,
    selectCredentials,
    selectSidePanelTitle,
    setCurrentPage
} from '../../store/pageLayoutSlice';
import {
    playButtonBig,
    playButtonBigGreen,
    playButtonSmall,
    playButtonSmallGreen,
    sectionComplete,
    sectionCompleteSmall,
    customerInfoIcon,
    activationDurationIcon,
    generalDetailsIcon,
    standardItemsIcon,
    miscellaneousItemsIcon
} from '../../assets/Icons/Icons';
import { apiCall } from '../../APICalls/APICalls';
import './CreateQuote.css';
import Tooltip from '../../components/Tooltip/Tooltip';

function areFieldsComplete(quoteSection){
    let complete = true;
    for ( let field in quoteSection){
        if ( quoteSection.hasOwnProperty(field) ){
            if ( field !== 'discount' && field !== 'customerAddress' && (!quoteSection[field] || quoteSection[ field ].trim() === '') ){
                complete = false;
                break;
            }
        }
    }
    return complete;
}

function areStdItemsComplete(lineItems){
    return lineItems.map( item => {
        if( item.productDescription.trim() !== '' ){
            return true;
        }
        if( item.productDescription.trim() === '' && ( item.discount.trim() !== '' || item.percentage.trim() !== '' || item.multiplier.trim() !== '' ) ){
            return false;
        }
    });
}

function areMiscItemsComplete(lineItems){
    return (
        lineItems.map( item => {
            if ( item.productDescription.trim() === '' ){
                return false;
            } else if ( item.totalPercentOff.trim() === '' && item.netPricing.trim() === '' ) {
                return false;
            } else {
                return true;
            }
        })
    );
}

function areMachinesComplete(machines){
    return (
        machines.map( machine => {
            if( machine.machineDescription.trim() !== '' && machine.rentalDuration.trim() === '' ){
                return false;
            } else if( machine.machineDescription.trim() === '' && machine.rentalDuration.trim() !== '' ){
                return false;
            } else {
                return true;
            }
        })
    );
}

const CreateQuote = props =>{
    const customerInformation = useSelector(selectCustomerInformation);
    const productDescriptions = useSelector(selectProductDescriptions);
    const activationDuration = useSelector(selectActivationDuration);
    const productInformation = useSelector(selectProductInformation);
    const machinesAndTools = useSelector(selectMachinesAndTools);
    const isQuoteRevision = useSelector(selectIsQuoteRevision);
    const generalDetails = useSelector(selectGeneralDetails);
    const machineEdit = useSelector(selectMachineEdit);
    const fullQuote = useSelector(selectCompleteQuote);
    const discounts = useSelector(selectDiscounts);
    const customers = useSelector(selectCustomers);
    const machines = useSelector(selectMachines);
    const asms = useSelector(selectASMs);
    const markets = useSelector(selectMarkets);
    const quoteNumber = fullQuote.quoteNumber;
    const loadingState = useSelector(selectLoadingStatus);
    const quoteNumberIsUnique = useSelector(selectUniqueQuoteNumber);
    const quotePrepared = useSelector(selectQuotePrepared);
    const credentials = useSelector(selectCredentials);
    const loading = loadingState.putQuote;
    const productsLoading = loadingState.productDescriptions;
    const dispatch = useDispatch();
    
    const activationDateValue = monthDayYearToIso(activationDuration.activationDate) || "";
    const expirationDateValue = monthDayYearToIso(activationDuration.expirationDate) || "";
    const dateValueObject = { activationDate: activationDateValue || "", expirationDate: expirationDateValue || "" };
    const productsList = productDescriptions.map( product => product.productDescription );
    const discountsList = [];
    const percentagesList = [];
    const multiplierList = [];

    const _customerName = customerInformation.customer;
    const _customerNumber = customerInformation.customerNumber;
    const customerNumbers = useMemo(() =>{
        let numbers;
        if( _customerName.trim() !== '' ){
            numbers = customers.filter( customer => customer.customer.toLowerCase().includes( _customerName.toLowerCase() ) ).map( c => formatCustomerNumber(c.customerNumber) );
        } else {
            numbers = customers.map( customer => formatCustomerNumber(customer.customerNumber) );
        }
        return numbers;
    }, [customers, _customerName]);

    const customerNames = useMemo(() => {
        let names;
        if( _customerNumber.trim() !== '' ){
            names = customers.filter( customer => customer.customerNumber.includes( _customerNumber ) ).map( c => c.customer );
        } else {
            names = customers.map( customer => customer.customer );
        }
        return [ ...new Set(names) ];
    }, [ customers, _customerNumber ]);

    const quoteSubmitObject = {
        dispatch: dispatch,
        completeQuote: fullQuote,
        customerInformation: customerInformation,
        productInformation: productInformation,
        credentials: credentials,
        isQuoteRevision: isQuoteRevision
    };

    discounts.forEach( discount => {
        discountsList.push( discount.discount );
        percentagesList.push( discount.percentage.toString() ); //object key used to be percentages
        multiplierList.push( discount.multiplier.toString() );
    });

    const machinesList = machines.map( machineObj => machineObj.machineDescription );
    const asmsList = asms.map( asm => asm.name );
    const marketsList = markets.map( market => market.market );

    useEffect(() => {
        // Fetch ASMs and Markets when component mounts
        dispatch( getAllASMs() );
        dispatch( getMarkets() );
    }, [dispatch]);

    useEffect(() => {
        if( isQuoteRevision ){
            const customerObj = customers.find( custObj => custObj.customerNumber === customerInformation.customerNumber );
            if( customerObj ){
                //console.log(customerObj);
                dispatch( getProductDescriptions({
                    //customerNumber: customerObj.customerNumber,
                    customerGroup: customerObj.customerGroup,
                    customerExecution: customerObj.customerExecution
                })).unwrap().then(res =>{
                    getMachineInfo(true);
                });
            }
        }
    }, [isQuoteRevision]);

    useEffect(() =>{
        if( loading === 'complete' ){
            // setTimeout(() => dispatch( setLoadingStatus( { field: 'putQuote', value: ''} ) ), 2000);
            // setTimeout(() => dispatch( clearQuote() ), 500);
            dispatch( clearQuote() );
            dispatch( setLoadingStatus( { field: 'putQuote', value: ''} ) );
            dispatch( setCurrentPage('Quotes') );
        } else if( loading === 'rejected' || loading === 'error' ){
            setTimeout(() => dispatch( setLoadingStatus( { field: 'putQuote', value: ''} ) ), 3000);
        }
    }, [ loading, dispatch ]);

    function submitQuote(){
        dispatch( putQuote( fullQuote ) );
        //console.log("COMPLETE QUOTE", JSON.stringify(fullQuote));
        //console.log("COMPLETE QUOTE OBJ", fullQuote);
    }

    useEffect(() =>{
        if( quotePrepared === true ){
            submitQuote();
        }
    }, [ quotePrepared, submitQuote ]);

    function updateStandardItem(e, index, field, eventType){
        let value = eventType === 'change' ? e.target.value : e.target.textContent;
        const payload = {
            value: value,
            index: index,
            field: field
        };
        dispatch( setStandardItem( payload ) );
    }

    function updateMachineItem(e, index, field, eventType){
        let value = eventType === 'change' ? e.target.value : e.target.textContent;
        const payload = {
            value: value,
            index: index,
            field: field
        };
        dispatch( setMachineRental( payload ) );
    }

    function updateCustomer(e, field, eventType){
        let payload = eventType === 'change' ? e.target.value : e.target.textContent;
        let customerObj;
        if( field === 'customerNumber' ){
            if( payload.length > 5 ){
                customerObj = customers.find( customer => {
                    let startIndex = customer.customerNumber.length - payload.length; //get index to start comparison from in string since. we are comparing from right to left due to leading zeros in customer number
                    let endIndex = customer.customerNumber.length;
                    return ( customer.customerNumber.slice(startIndex, endIndex) === payload );
                });
            }
        } else {
            let customerObjectArray = customers.filter(customer => customer.customer.trim().toLowerCase() === payload.trim().toLowerCase());
            if( customerObjectArray.length === 1 ){
                customerObj = customerObjectArray[0];
            }
        }
        if( customerObj ){
            //console.log(customerObj);
            dispatch( setCustomer(customerObj) );
            dispatch( getProductDescriptions({
                //customerNumber: customerObj.customerNumber,
                customerGroup: customerObj.customerGroup,
                customerExecution: customerObj.customerExecution
            }));
        } else {
            const dispatchObj = {customerNumber: '', customer: ''};
            dispatchObj[field] = payload;
            dispatch( setCustomer( dispatchObj ) );
        }
    }

    function getMachineInfo(shouldFetch){
        if( shouldFetch ){
            let productHierarchies = []
            productInformation.standardItems.forEach( item => productHierarchies.push( ...item.productHierarchies ));
            dispatch( getMachines({ productHierarchies: productHierarchies }) );
        }
    }

    function handleReset(){
        if( isQuoteRevision === true ){
            dispatch( revisionReset() );
        } else {
            dispatch( clearQuote() );
        }
    }

    const standardItems = productInformation.standardItems.map((item, i) =>{
        // const productDetailsObj = customerObj?.product_descriptions?.filter( description => description.product_desc === item.productDescription )[0] || [];
        return (
            <li className="std_item" key={i}>
                <div className="std_product_description">
                    <Input 
                        type="searchable"
                        value={ item.productDescription || ''}
                        handleChange={ 
                            (e) => updateStandardItem(e, i, 'productDescription', 'change')
                        }
                        handleSelect={
                            (e) => updateStandardItem(e, i, 'productDescription', 'select')
                        }
                        handleClearValue={
                            () => dispatch( setStandardItem( { value: '', index: i, field: 'productDescription' } ) )
                        }
                        options={ productsList || [] }
                    />
                </div>
                <div className="std_std_discount">
                    <Input 
                        type="display"
                        // options={ customersList }
                        // handleChange={
                        //     (e) =>  updateCustomer(e, 'customer', 'change')
                        // }
                        // handleSelect={ 
                        //     (e) => updateCustomer(e, 'customer', 'select')
                        // }
                        value={ item.stdDiscount || '' }
                        disabled={ true }
                    />
                </div>
                <div className="std_discount">
                    <Input 
                        type="searchable"
                        value={ item.discount || '' }
                        handleChange={ 
                            (e) => updateStandardItem(e, i, 'discount', 'change')
                        }
                        handleSelect={
                            (e) => updateStandardItem(e, i, 'discount', 'select')
                        }
                        handleClearValue={
                            () => dispatch( setStandardItem( { value: '', index: i, field: 'discount' } ) )
                        }
                        options={ discountsList || [] }
                    />
                </div>
                <div className="std_percentage">
                    <Input 
                        type="searchable"
                        value={ item.percentage || '' }
                        handleChange={ 
                            (e) => updateStandardItem(e, i, 'percentage', 'change')
                        }
                        handleSelect={
                            (e) => updateStandardItem(e, i, 'percentage', 'select')
                        }
                        handleClearValue={
                            () => dispatch( setStandardItem( { value: '', index: i, field: 'percentage' } ) )
                        }
                        options={ percentagesList || [] }
                    />
                </div>
                <div className="std_multiplier">
                    <Input 
                        type="searchable"
                        value={ item.multiplier || '' }
                        handleChange={ 
                            (e) => updateStandardItem(e, i, 'multiplier', 'change')
                        }
                        handleSelect={
                            (e) => updateStandardItem(e, i, 'multiplier', 'select')
                        }
                        handleClearValue={
                            () => dispatch( setStandardItem( { value: '', index: i, field: 'multiplier' } ) )
                        }
                        options={ multiplierList || [] }
                    />
                </div>
            </li>
        );
    });

    const miscellaneousItems = productInformation.miscellaneousItems.map((item, i) =>{
        return (
            <li className="misc_item" key={i}>
                <div className="misc_product_description">
                    <Input 
                        type="text"
                        value={ item.productDescription || ''}
                        handleChange={ 
                            (e) => dispatch(
                                    setMiscellaneousItem({ 
                                        value: e.target.value,
                                        index: i,
                                        field: 'productDescription'
                                    })
                                )
                        }
                    />
                </div>
                <div className="misc_total_percent_off">
                    <Input 
                        type="text"
                        value={ item.totalPercentOff || '' }
                        handleChange={ 
                            (e) => dispatch(
                                    setMiscellaneousItem({ 
                                        value: e.target.value,
                                        index: i,
                                        field: 'totalPercentOff'
                                    })
                                )
                        }
                    />
                </div>
                <div className="misc_net_pricing">
                    <Input 
                        type="text"
                        value={ item.netPricing || '' }
                        handleChange={ 
                            (e) => dispatch(
                                    setMiscellaneousItem({ 
                                        value: e.target.value,
                                        index: i,
                                        field: 'netPricing'
                                    })
                                )
                        }
                    />
                </div>
            </li>
        );
    });

    const machineRentals = machinesAndTools.map((machineRental, i) =>{
        let machineObj;
        let durations = [];
        if( machineRental.machineDescription.trim() !== '' ){
            machineObj = machines.find( obj => obj.machineDescription === machineRental.machineDescription.trim() );
            if( machineObj ){
                durations = machineObj.rentalParameters.map( param => param.duration );
            }
        }
        return (
            <li className="machine_list_item" key={i}>
                {
                    machineEdit ?
                    <>
                        <div className="machine_information">
                            <Input
                                label="Machine Type" 
                                type="searchable"
                                options={ machinesList }
                                handleChange={ 
                                    (e) => updateMachineItem(e, i, 'machineDescription', 'change')
                                }
                                handleSelect={ 
                                    (e) => updateMachineItem(e, i, 'machineDescription', 'select')
                                }
                                handleClearValue={
                                    () => dispatch( setMachineRental( { value: '', index: i, field: 'machineDescription' } ) )
                                }
                                value={ machineRental.machineDescription }
                            />
                        </div>
                        <div className="machine_information">
                            <Input
                                label="Rental Duration" 
                                type="searchable"
                                options={ durations }
                                handleChange={ 
                                    (e) => updateMachineItem(e, i, 'rentalDuration', 'change')
                                }
                                handleSelect={ 
                                    (e) => updateMachineItem(e, i, 'rentalDuration', 'select')
                                }
                                handleClearValue={
                                    () => dispatch( setMachineRental( { value: '', index: i, field: 'rentalDuration' } ) )
                                }
                                value={ machineRental.rentalDuration }
                            />
                        </div>
                        <div className="machine_item_clear" onClick={ () => dispatch( clearMachineItem(i) ) }>
                            <Clear />
                        </div>
                    </>
                    :
                    <>
                        <div className="machine_information">
                            <Input
                                label="Machine Type" 
                                type="display"
                                value={ machineRental.machineDescription }
                            />
                        </div>
                        <div className="machine_information">
                            <Input
                                label="Rental Duration" 
                                type="display"
                                value={ machineRental.rentalDuration }
                            />
                        </div>
                    </>
                }
            </li>
        );
    });
    //const quoteNumberComplete = quoteNumber.trim() !== '';
    const quoteNumberComplete = true;
    const customerInfoComplete = areFieldsComplete( customerInformation );
    const activationDurationComplete = areFieldsComplete( activationDuration );
    const generalDetailsComplete = areFieldsComplete( generalDetails ) && quoteNumberComplete;
    const generalInfoComplete = ( customerInfoComplete && activationDurationComplete && generalDetailsComplete );
    const stdItemsComplete = areStdItemsComplete( productInformation.standardItems ).includes(true);
    const miscItemsComplete = areMiscItemsComplete( productInformation.miscellaneousItems ).includes(true);
    const productInfoComplete = ( stdItemsComplete || miscItemsComplete );
    let machinesAndToolsComplete = ( !machineEdit && productInfoComplete && areMachinesComplete(machinesAndTools).includes(true) );

    // const listHeaderRef = useRef(null);
    // const toolTipSpanRef = useRef(null);
    // const windowWidth = window.innerWidth;
    // const toolTipDistance = useMemo(()=>{
    //     if( listHeaderRef.current && toolTipSpanRef.current ){

    //     } else {
    //         return '45%';
    //     }
    // }, [listHeaderRef, toolTipSpanRef]);

    return (
        <div id="create_quote_container">
            <Accordion
                contentDescription="General Information"
                headContent={ <p className="outer_section_title">General Information</p> }
                isTopAccordion={ false }
                iconLeft={ generalInfoComplete ? sectionComplete : playButtonBigGreen }
                iconLeftAnimate={ !generalInfoComplete }
                shouldClose={ (loading === 'complete') }
            >
                <Accordion
                    contentDescription="Customer Information"
                    headContent={ <p className="inner_section_title">Customer Information</p> }
                    isTopAccordion={ false }
                    iconLeft={ customerInfoIcon }
                    iconRight={ customerInfoComplete ? sectionCompleteSmall : playButtonSmallGreen }
                    iconRightAnimate={ !customerInfoComplete }
                    isOpenClass="highest_accordion"
                >
                    <p className={ productsLoading === 'rejected' ? "create_quote_warning" : "create_quote_warning _hidden"}>There was a problem retrieving the customers available products.</p>
                    <div id="customer_information_container" >
                        <div className="customer_information">
                            <Input
                                label="Customer Number" 
                                type="searchable"
                                options={ customerNumbers }
                                handleChange={
                                    (e) =>  updateCustomer(e, 'customerNumber', 'change')
                                }
                                handleSelect={ 
                                    (e) => updateCustomer(e, 'customerNumber', 'select')
                                }
                                handleClearValue={
                                    () => dispatch( setCustomer( { customerNumber: '', customer: ''} ) )
                                }
                                value={ formatCustomerNumber( customerInformation.customerNumber ) }
                                disabled={ isQuoteRevision }
                                isRequired={ true }
                            />
                        </div>
                        <div className="customer_information">
                            <Input
                                label="Customer"
                                type="searchable"
                                options={ customerNames }
                                handleChange={
                                    (e) =>  updateCustomer(e, 'customer', 'change')
                                }
                                handleSelect={
                                    (e) => updateCustomer(e, 'customer', 'select')
                                }
                                handleClearValue={
                                    () => dispatch( setCustomer( { customerNumber: '', customer: ''} ) )
                                }
                                value={ customerInformation.customer }
                                disabled={ isQuoteRevision }
                                isRequired={ true }
                            />
                            {/* <Input
                                label="Customer"
                                type="display"
                                // options={ customersList }
                                // handleChange={
                                //     (e) =>  updateCustomer(e, 'customer', 'change')
                                // }
                                // handleSelect={
                                //     (e) => updateCustomer(e, 'customer', 'select')
                                // }
                                value={ customerInformation.customer || ' ' }
                            /> */}
                        </div>
                    </div>
                </Accordion>
                <Accordion
                    contentDescription="Activation Duration"
                    headContent={ <p className="inner_section_title">Activation Duration</p> }
                    iconLeft={ activationDurationIcon }
                    iconRight={ activationDurationComplete ? sectionCompleteSmall : customerInfoComplete ? playButtonSmallGreen : playButtonSmall }
                    iconRightAnimate={ !activationDurationComplete }
                    isOpenClass="middle_accordion"
                >
                    <div id="activation_duration_container">
                        <div className="activation_duration">
                            <Input
                                label="Activation Date" 
                                type="date"
                                handleChange={
                                    (value) =>  dispatch( setActivationDate( value ) )
                                }
                                value={activationDateValue}
                                disabled={ isQuoteRevision }
                                min={generateMinOrMaxDate(true)}
                                max={expirationDateValue || generateMinOrMaxDate()}
                                isRequired={ true }
                                handleBlur={ (e) => handleDateBlur(e, dispatch, dateValueObject, true) }
                            />
                        </div>
                        <div className="activation_duration">
                            <Input
                                label="Expiration Date" 
                                type="date"
                                handleChange={
                                    (value) =>  dispatch( setExpirationDate( value ) )
                                }
                                value={expirationDateValue}
                                min={activationDateValue || generateMinOrMaxDate(true)}
                                max={generateMinOrMaxDate()}
                                isRequired={ true }
                                handleBlur={ (e) => handleDateBlur(e, dispatch, dateValueObject, false) }
                            />
                        </div>
                    </div>
                </Accordion>
                <Accordion
                    contentDescription="General Details"
                    headContent={ <p className="inner_section_title">General Details</p> }
                    iconLeft={ generalDetailsIcon }
                    iconRight={ generalDetailsComplete ? sectionCompleteSmall : activationDurationComplete ? playButtonSmallGreen : playButtonSmall }
                    iconRightAnimate={ !generalDetailsComplete }
                    isOpenClass="lowest_accordion"
                >
                    <div id="general_details_container" className="general_details_content">
                        {
                            <p className={quoteNumberIsUnique === false ? "unique_quotenum_warning" : "unique_quotenum_warning _hidden"}>Please choose a unique quote number.</p>
                        }
                        <div className="general_details_flex_container">
                            <div className="general_details_one">
                                <Input
                                    label="ASM"
                                    type="searchable"
                                    options={ asmsList }
                                    handleChange={
                                        (e) => {
                                            const asmName = e.target.value;
                                            const asmObj = asms.find(asm => asm.name === asmName);
                                            if (asmObj) {
                                                dispatch( setASM( asmObj ) );
                                            }
                                        }
                                    }
                                    handleSelect={
                                        (e) => {
                                            const asmName = e.target.textContent;
                                            const asmObj = asms.find(asm => asm.name === asmName);
                                            if (asmObj) {
                                                dispatch( setASM( asmObj ) );
                                            }
                                        }
                                    }
                                    handleClearValue={
                                        () => dispatch( setASM( { name: '', SAP: '', email: '' } ) )
                                    }
                                    value={ generalDetails.asm }
                                    isRequired={ true }
                                />
                            </div>
                            <div className="general_details_one">
                                <Input
                                    label="Market"
                                    type="searchable"
                                    options={ marketsList }
                                    handleChange={
                                        (e) => dispatch( setMarket( e.target.value ) )
                                    }
                                    handleSelect={
                                        (e) => dispatch( setMarket( e.target.textContent ) )
                                    }
                                    handleClearValue={
                                        () => dispatch( setMarket( '' ) )
                                    }
                                    value={ generalDetails.market }
                                    isRequired={ true }
                                />
                            </div>
                            <div className="general_details_one">
                                <Input
                                    label="Attention To"
                                    type="text"
                                    handleChange={
                                        (e) =>  dispatch( setAttn( e.target.value ) )
                                    }
                                    value={ customerInformation.attn }
                                    isRequired={ true }
                                />
                            </div>
                        </div>
                        <div className="general_details_flex_container">
                            <div className="general_details_one">
                                <Input
                                    label="Opportunity Number" 
                                    type="text"
                                    handleChange={
                                        (e) =>  dispatch( setOpportunityNumber( e.target.value ) )
                                    }
                                    value={ generalDetails.opportunityNumber }
                                    disabled={ isQuoteRevision }
                                    isRequired={ true }
                                />
                            </div>
                            <div className="general_details_one">
                                <Input
                                    label="Estimated Total Value" 
                                    type="text"
                                    handleChange={
                                        (e) =>  dispatch( setEstimatedTotalValue( e.target.value ) )
                                    }
                                    value={ generalDetails.estimatedTotalValue }
                                    isRequired={ true }
                                />
                            </div>
                            <div className="general_details_one">
                                <Input
                                    label="Quote Number"
                                    type="text"
                                    handleChange={
                                        (e) =>  dispatch( setQuoteNumber( e.target.value ) )
                                    }
                                    value={ quoteNumber }
                                    handleBlur={
                                        () => dispatch( checkQuoteNumber( { quoteNumber: quoteNumber } ) )
                                    }
                                />
                            </div>
                        </div>
                        <div className="general_details_two">
                            <Input
                                label="Quote Subject" 
                                type="text"
                                handleChange={
                                    (e) =>  dispatch( setQuoteSubject( e.target.value ) )
                                }
                                value={ generalDetails.quoteSubject }
                                disabled={ isQuoteRevision }
                                isRequired={ true }
                            />
                        </div>
                        <div className="general_details_two">
                            <Input
                                label="Additional Notes" 
                                type="textarea"
                                handleChange={
                                    (e) =>  dispatch( setAdditionalNotes( e.target.value ) )
                                }
                                value={ fullQuote.quote[0].additionalNotes }
                            />
                        </div>
                    </div>
                </Accordion>
            </Accordion>
            <Accordion
                contentDescription="Product Information"
                headContent={ <p className="outer_section_title">Product Information</p> }
                isTopAccordion={ false}
                iconLeft={ productInfoComplete ? sectionComplete : generalInfoComplete ? playButtonBigGreen : playButtonBig }
                iconLeftAnimate={ !productInfoComplete }
                disabled={ productsList.length === 0 }
                shouldClose={ ( (loading === 'complete') || !customerInfoComplete ) }
            >
                <Accordion
                    contentDescription="Standard Items"
                    headContent={ <p className="inner_section_title">Standard Items</p> }
                    isTopAccordion={ false }
                    iconLeft={ standardItemsIcon }
                    iconRight={ stdItemsComplete ? sectionCompleteSmall : generalInfoComplete ? playButtonSmallGreen : playButtonSmall }
                    iconRightAnimate={ !stdItemsComplete }
                    isOpenClass="highest_accordion"
                >
                    <div id="standard_items_container" className="items_list_container">
                        <ul id="standard_items_list" className="items_list">
                            <li className="items_list_headers">
                                <span className="item_header std_product_description">Product Description</span>
                                <span className="item_header std_std_discount">Std. Discount</span>
                                <span className="item_header std_discount">Discount</span>
                                <span className="item_header std_percentage">Percentage</span>
                                <span className="item_header std_multiplier">Multiplier</span>
                            </li>
                            { standardItems }
                        </ul>
                        <div className="additional_item_container">
                            <button className="additional_item" onClick={ () => dispatch( addStandardItem() ) }>
                                + Additional Item
                            </button>
                        </div>
                    </div>
                </Accordion>
                <Accordion
                    contentDescription="Miscellaneous Items"
                    headContent={ <p className="inner_section_title">Miscellaneous Items</p> }
                    iconLeft={ miscellaneousItemsIcon }
                    iconRight={ miscItemsComplete ? sectionCompleteSmall : generalInfoComplete ? playButtonSmallGreen : playButtonSmall }
                    iconRightAnimate={ !miscItemsComplete }
                    isOpenClass="middle_accordion"
                >
                    <div id="miscellaneous_items_container" className="items_list_container">
                        <ul id="miscellaneous_items_list" className="items_list">
                            <li className="items_list_headers">
                                <span className="item_header misc_product_description">
                                    Product Description
                                </span>
                                <span className="item_header misc_total_percent_off">
                                    <div className="item_header_tooltip_container">
                                        <span>Total % Off</span>
                                        <span className="miscellaneous_tooltip_span">
                                            <Tooltip
                                                size={12}
                                                color="#000000"
                                                strokeWidth={1.1}
                                                fontSize={12}
                                                tip="Choose either total % off or net pricing for each product description."
                                                // alignment = 'right'
                                                tipStyles={
                                                    {
                                                        position: 'absolute',
                                                        right: '0',
                                                        top: '115%',
                                                        backgroundColor: '#ffffff',
                                                        fontSize: '12px',
                                                        zIndex: '100',
                                                        border: '1px solid gray',
                                                        borderRadius: '3px',
                                                        padding: '5px',
                                                        boxShadow: '1px 1px 2px 1px rgba(0, 0, 0, 0.3)',
                                                        fontWeight: 'normal',
                                                        display: 'block',
                                                        textAlign: 'left'
                                                    }
                                                }
                                            />
                                        </span>
                                    </div>
                                </span>
                                <span className="item_header misc_net_pricing">
                                <div className="item_header_tooltip_container">
                                        <span>Net Pricing</span>
                                        <span className="miscellaneous_tooltip_span">
                                            <Tooltip
                                                size={12}
                                                color="#000000"
                                                strokeWidth={1.1}
                                                fontSize={12}
                                                tip="Choose either total % off or net pricing for each product description."
                                                // alignment = 'right'
                                                tipStyles={
                                                    {
                                                        position: 'absolute',
                                                        right: '0',
                                                        top: '115%',
                                                        backgroundColor: '#ffffff',
                                                        fontSize: '12px',
                                                        zIndex: '100',
                                                        border: '1px solid gray',
                                                        borderRadius: '3px',
                                                        padding: '5px',
                                                        boxShadow: '1px 1px 2px 1px rgba(0, 0, 0, 0.3)',
                                                        fontWeight: 'normal',
                                                        display: 'block',
                                                        textAlign: 'left'
                                                    }
                                                }
                                            />
                                        </span>
                                    </div>
                                </span>
                            </li>
                            { miscellaneousItems }
                        </ul>
                        <div className="additional_item_container">
                            <button className="additional_item" onClick={ () => dispatch( addMiscellaneousItem() ) }>
                                + Additional Item
                            </button>
                        </div>
                    </div>
                </Accordion>
            </Accordion>
            <Accordion
                contentDescription="Machines and Tools"
                headContent={ <p className="outer_section_title">Machines and Tools</p> }
                isTopAccordion={ false }
                iconLeft={ machinesAndToolsComplete ? sectionComplete : productInfoComplete ? playButtonBigGreen : playButtonBig }
                iconLeftAnimate={ !machinesAndToolsComplete }
                disabled={ !productInfoComplete }
                onClick={ (isOpen) => getMachineInfo(isOpen) }
                shouldClose={ ( (loading === 'complete') || !productInfoComplete ) }
                isOpenClass="highest_accordion"
            >
                <div>
                    <div className={ machineEdit ? "machine_warning_container" : "machine_warning_container _hidden" }>
                        <p className="create_quote_machine_warning">*Machine rental changes and/or additions may be subject to approval.</p>
                    </div>
                    <div id="machine_rental_container">
                        <ul id="machine_rentals_list">
                            { machineRentals }
                        </ul>
                        <div className="machine_rental_edit_button_container">
                            <button
                                className="machine_rental_edit_button"
                                onClick={ () => dispatch( setMachineRentalEdit() ) }
                            >
                                { machineEdit ? "Done" : "Edit" }
                            </button>
                        </div>
                    </div>
                    <div className={ machineEdit ? "additional_machine_container" : "hidden additional_machine_container" }>
                        <button className="additional_item" onClick={ () => dispatch( addMachineRental() ) }>
                            + Additional Machine
                        </button>
                    </div>
                </div>
            </Accordion>
            <Accordion
                contentDescription="Quote Review"
                headContent={
                    <div className="quote_review_head_content">
                        <p className="outer_section_title">Quote Review</p>
                        <div className="create_quote_button_container">
                            <button
                                className="create_quote_button reset_button"
                                onClick={ handleReset }
                            >
                            Reset
                            </button>
                        </div>
                    </div>
                }
                isTopAccordion={ false }
                iconLeft={ productInfoComplete && generalInfoComplete ? playButtonBigGreen : playButtonBig  }
                iconLeftAnimate={ true }
                disabled={ !generalInfoComplete || !productInfoComplete }
                shouldClose={ ( (loading === 'complete') || (!generalInfoComplete || !productInfoComplete) ) }
                isOpenClass="middle_accordion"
            >
                <div id="quote_review_container">
                    <Quote view="create_quote" quote={ fullQuote } />
                    <div className="">
                        {
                            generalInfoComplete && machinesAndToolsComplete && quoteNumberIsUnique &&
                            <div className="create_quote_button_container">
                                <button
                                    className="create_quote_button create_button"
                                    onClick={ () => quotePreSubmit( quoteSubmitObject ) }
                                >
                                { isQuoteRevision ? 'Save Changes' : 'Create Quote' }
                                </button>
                            </div>
                        }
                        {/* <div className="create_quote_button_container">
                            <button
                                className="create_quote_button reset_button"
                                onClick={ () => dispatch( clearQuote() ) }
                            >
                            Reset
                            </button>
                        </div> */}
                    </div>
                </div>
            </Accordion>
            {
                (loading === 'loading' || productsLoading === 'loading') && //when loading === complete, cleanup occurs. when idle, loading === ''
                <Loader />
            }   
        </div>
    );
}

function handleDateBlur(e, dispatch, datesObject, isActivationDate){
    let millis, dateString = "";
    if( e.target.valueAsNumber ){
        millis = e.target.valueAsNumber;
    }
    if( millis ){ //date is valid and exists
        let rangeMillis = 157766400000; //5 years and one day (leap day) in milliseconds
        let minMillis, maxMillis, dateStringArray, date;
        if( isActivationDate === true ){ //function being called from activation date input
            minMillis = Date.now() - rangeMillis;
            if( datesObject.expirationDate.trim() !== '' ){
                dateStringArray = datesObject.expirationDate.trim().split("-");
                date = new Date(); //documentation recommends manually setting date values instead of constructing with string
                date.setMonth( Number(dateStringArray[1]) - 1 );
                date.setDate( Number(dateStringArray[2]) );
                date.setFullYear( Number(dateStringArray[0]) );
                maxMillis = date.getTime();
            } else {
                maxMillis = Date.now() + rangeMillis;
            }
        } else { //function being called from expiration date input
            maxMillis = Date.now() + rangeMillis;
            if( datesObject.activationDate.trim() !== '' ){
                dateStringArray = datesObject.activationDate.trim().split("-");
                date = new Date(); //documentation recommends manually setting date values instead of constructing with string
                date.setMonth( Number(dateStringArray[1]) - 1 );
                date.setDate( Number(dateStringArray[2]) );
                date.setFullYear( Number(dateStringArray[0]) );
                minMillis = date.getTime();
            } else {
                minMillis = Date.now() - rangeMillis;
            }
        }
        if( millis <= maxMillis && millis >= minMillis ){ //date is valid, leave as is
            dateString = e.target.value;
        } else {
            if( millis > maxMillis ){
                dateString = createISOdateString( new Date( maxMillis ) );
            } else {
                dateString = createISOdateString( new Date( minMillis ) );
            }
        }
    } else {
        e.target.value = '';
    }
    if( isActivationDate === true ){
        dispatch( setActivationDate( dateString ) );
    } else {
        dispatch( setExpirationDate( dateString) );
    }
}

const Clear = props =>{
	return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px" fill="none" stroke="#505050" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
	);
}

async function quotePreSubmit({dispatch, completeQuote, customerInformation, productInformation, credentials, isQuoteRevision}){
    try{
        let usersToNotify = [];
        let nonASMs = []; //array of users being notified that are not ASMS

        // Add the selected ASM to usersToNotify if one was selected
        if( completeQuote.quote[0].asm && completeQuote.quote[0].asm.name ){
            usersToNotify.push({
                userId: completeQuote.quote[0].asm.SAP,
                userName: completeQuote.quote[0].asm.name,
                email: completeQuote.quote[0].asm.email || '',
                role: 'ASM'
            });
        }

        const doaResponse = await apiCall('quotesAndNotifications/evaluateDoA', { quote: completeQuote.quote[0] }); //what needs approval under the Delegation of Authority
        const { flagsArr, rolesArr, notificationType, newFlagsIds } = identifyAndCreateFlags(completeQuote, doaResponse.data.flags, isQuoteRevision); //get flags and roles to notify
        const roleResponses = await Promise.all( rolesArr.map( roleObj => apiCall('quotesAndNotifications/getUsersByRole', roleObj) ) ); //get all users to notify by role
        console.log(roleResponses);
        roleResponses.forEach((response, i) => {
            if( response.data.length > 0 ){
                nonASMs.push( //add users to array
                    {
                        role: rolesArr[i].role,
                        userId: response.data[0].userId?.toString(),
                        email: response.data[0].email
                    }
                );
            }
        });

        const quoterObj = {
            userId: credentials.userId?.toString(),
            email: credentials.email,
            role: credentials.userType.role
        };
        let userObj;
        console.log(nonASMs);
        // Only populate chainOfCommand for new flags (old preserved flags already have this info)
        flagsArr.forEach((flag, i) => {
            if( newFlagsIds.includes(flag.id) ){ // Only process new flags
                flag.chainOfCommand.forEach(rank =>{
                    console.log(rank);
                    userObj = nonASMs.find( userobj => userobj.role === rank.role );
                    console.log(userObj);
                    if( userObj ){ // Add safety check
                        rank.userId = userObj.userId;
                        rank.userName = userObj.userName;
                    }
                });
            }
        });
        nonASMs.push(quoterObj); //add quoter to users to nonASM users to notify
        usersToNotify = [...usersToNotify, ...nonASMs]; //combine array of all users to notify including asms
        if( flagsArr.length !== 0 ){ //quote status and notification status
            dispatch( setFlags( flagsArr ) ); //set flags
        }
        dispatch( setNotifications({ users: usersToNotify, type: notificationType }) );
        dispatch( prepareQuote({ quoterId: quoterObj.userId, email: credentials.email, userName: credentials.userName, quoteStatus: notificationType }) );
        dispatch( setQuotePrepared(true) );
    } catch(e){
        console.log(e);
    }
}

function resizeSVG(){
    let parentDiv = document.getElementById("progressBarContainer");
    let svgs = parentDiv.getElementsByTagName("svg");
    if( svgs.length > 2 ){ //make sure progress bar is being resized
        let width = parentDiv.clientWidth;
        let viewBox;
        for( let i = 0; i < svgs.length; i++ ){
            viewBox = `0 0 ${width} ${svgs[i].getAttribute("height")}`;
            svgs[i].setAttribute("width", width);
            svgs[i].setAttribute("viewBox", viewBox);
        }
        let foreignObjects = document.getElementsByClassName("progress_foreign_obj");
        width = width - 40 > 0 ? width - 40 : 0;
        for( let i = 0; i < foreignObjects.length; i++ ){
            foreignObjects[i].setAttribute("width", width);
            foreignObjects[i].firstChild.style.maxWidth = `${width}px`;
        }
    }
}

function confirmationMessage(loadingStatus, isQuoteRevision){
    let message;
    //let quoteType = isQuoteRevision ? 'Quote revision' : 'Quote'; 
    let quoteType = 'Quote';
    if( loadingStatus === 'complete' ){
        message = quoteType + ' successfully created!';
    } else if( loadingStatus === 'error' ){
        message = 'There was an error during creation of the ' + quoteType.toLocaleLowerCase() + '.';
    } else if( loadingStatus === 'rejected' ){
        message = 'There was a problem connecting to the server. Please try again.';
    }
    return message;
}

const CreateQuoteSPC = props =>{

    const divRef = useRef(null);
    const dispatch = useDispatch();

    const sidePanelTitle = useSelector(selectSidePanelTitle);
    const isQuoteRevision = useSelector(selectIsQuoteRevision);
    const customerInformation = useSelector(selectCustomerInformation);
    const generalDetails = useSelector(selectGeneralDetails);
    const productInformation = useSelector(selectProductInformation);
    const activationDuration = useSelector(selectActivationDuration);
    const machinesAndTools = useSelector(selectMachinesAndTools);
    const machineEdit = useSelector(selectMachineEdit);
    const completeQuote = useSelector(selectCompleteQuote);
    const machines = useSelector(selectMachines);
    const quoteNumberIsUnique = useSelector(selectUniqueQuoteNumber);
    const loading = useSelector(selectLoadingStatus).putQuote;
    const quoteNumber = completeQuote.quoteNumber;

    useEffect(() => {
        let title;
        if( isQuoteRevision && (completeQuote.quote[0].suggestions.length > 0 || completeQuote.quote[0].modifications.length > 0) ){
            title = 'Messages';
        } else {
            title = 'Progress';
        }
        dispatch( setSidePanelTitle(title) );
    }, [ dispatch, isQuoteRevision ]);

    useEffect(() => {
        window.addEventListener("resize", resizeSVG);
        return () => window.removeEventListener("resize", resizeSVG);
    }, []);

    useEffect(() => {
        if( divRef ){
            resizeSVG();
        }
    }, [ divRef, sidePanelTitle, productInformation ]);

    useEffect(() => {
        let options = ['Progress', 'Account'];
        if( isQuoteRevision ){
            options.unshift('Messages');
        }
        dispatch( setSidePanelOptions( options ) );
    }, [ isQuoteRevision, dispatch ]);

    //const quoteNumberComplete = quoteNumber.trim() !== '';
    const quoteNumberComplete = true;
    const customerInfoComplete = areFieldsComplete( customerInformation );
    const activationDurationComplete = areFieldsComplete( activationDuration );
    const generalDetailsComplete = areFieldsComplete( generalDetails ) && quoteNumberComplete;
    const generalInfoComplete = ( customerInfoComplete && activationDurationComplete && generalDetailsComplete );
    const stdItemsComplete = areStdItemsComplete( productInformation.standardItems ).includes(true);
    const miscItemsComplete = areMiscItemsComplete( productInformation.miscellaneousItems ).includes(true);
    const productInfoComplete = ( stdItemsComplete || miscItemsComplete );
    const machinesAndToolsComplete = ( !machineEdit && productInfoComplete && areMachinesComplete(machinesAndTools).includes(true) );

    const gray = "#939393";
    const green = "#17a517";
    const black = "#000000";
    const normFont = "SegoeUI, Segoe UI";
    const boldFont = "SegoeUI-Semibold, Segoe UI";
    const fSize = "600";

    let customerFill = "none", actDateFill = "none", expDateFill = "none", oppNumFill = "none", estTotValFill = "none", subjectFill = "none", quoteNumFill = "none";
    let actDateStroke = gray, expDateStroke = gray, oppNumStroke = gray, estTotValStroke = gray, subjectStroke = gray, productInfoStroke = gray, machinesStroke = gray, reviewStroke = gray, quoteNumStroke = gray;
    let actDateFontFam = normFont, expDateFontFam = normFont, oppNumFontFam = normFont, estTotValFontFam = normFont, subjectFontFam = normFont, quoteNumFontFam = normFont;
    let actDateFontWeight, expDateFontWeight, oppNumFontWeight, estTotValFontWeight, subjectFontWeight, quoteNumFontWeight;
    let actDateFontFill = gray, expDateFontFill = gray, oppNumFontFill = gray, estTotValFontFill = gray, subjectFontFill = gray, productInfoFill = gray, machinesFill = gray, reviewFill = gray, quoteNumFontFill = gray;

    if( customerInformation.customer.trim() !== '' ){
        customerFill = actDateStroke = green;
        actDateFontFam = boldFont;
        actDateFontWeight = fSize;
        actDateFontFill = black;
    }

    if( activationDuration.activationDate && activationDuration.activationDate.trim() !== '' ){
        actDateFill = expDateStroke = green;
        expDateFontFam = boldFont;
        expDateFontWeight = fSize;
        expDateFontFill = black;
    }

    if( activationDuration.expirationDate && activationDuration.expirationDate.trim() !== '' ){
        expDateFill = oppNumStroke = green;
        oppNumFontFam = boldFont;
        oppNumFontWeight = fSize;
        oppNumFontFill = black;
    }

    if( generalDetails.opportunityNumber.trim() !== '' ){
        oppNumFill = estTotValStroke = green;
        estTotValFontFam = boldFont;
        estTotValFontWeight = fSize;
        estTotValFontFill = black;
    }

    if( generalDetails.estimatedTotalValue.trim() !== '' ){
        estTotValFill = quoteNumStroke = green;
        quoteNumFontFam = boldFont;
        quoteNumFontWeight = fSize;
        quoteNumFontFill = black;
    }

    if( quoteNumberComplete && generalDetails.estimatedTotalValue.trim() !== '' ){
        quoteNumFill = subjectStroke = green;
        subjectFontFam = boldFont;
        subjectFontWeight = fSize;
        subjectFontFill = black;
    }
    
    if( generalDetails.quoteSubject.trim() !== '' ){
        subjectFill = green;
    }

    if( generalInfoComplete ){
        productInfoStroke = green;
        productInfoFill = black;
    }

    const productEllipses = [];
    const machineEllipses = [];

    productInformation.standardItems.forEach((item, i) => {
        if( item.productDescription.trim() !== '' ){
            productEllipses.push(
                <EllipseAndLine
                    label="Product Desc."
                    value={ item.productDescription }
                    lineStroke="#17a517"
                    ellipseFill="#17a517"
                    key={ item.productDescription + i }
                />
            );
        }
    });

    productInformation.miscellaneousItems.forEach((item, i) => {
        if( item.productDescription.trim() !== '' ){
            productEllipses.push(
                <EllipseAndLine
                    label="Product Desc."
                    value={ item.productDescription }
                    lineStroke="#17a517"
                    ellipseFill="#17a517"
                    key={ item.productDescription + i }
                />
            );
        }
    });

    machinesAndTools.forEach((item, i)=>{
        if( item.machineDescription.trim() !== '' ){
            machineEllipses.push(
                <EllipseAndLine
                    label="Machine Desc."
                    value={ item.machineDescription }
                    lineStroke="#17a517"
                    ellipseFill="#17a517"
                    key={ item.machineDescription + i }
                />
            );
        }
    });

    if( productEllipses.length > 0 ){
        machinesStroke = green;
        machinesFill = black;
    }

    if( generalInfoComplete && productEllipses.length > 0 ){
        reviewStroke = green;
        reviewFill = black;
    }

    return (
        <div className="create_quote_sp_container" ref={ divRef } id="progressBarContainer">
            {
                sidePanelTitle === 'Progress' &&
                <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="188" height="256" viewBox="0 0 188 256">
                        <path id="Path_287" data-name="Path 287" d="M0,1.041v11" transform="translate(15 28.5)" fill="none" stroke="#17a517" strokeWidth="3"/>
                        <path id="Path_286" data-name="Path 286" d="M0,.23v11" transform="translate(15 54.5)" fill="none" stroke={actDateStroke} strokeWidth="3"/>
                        <path id="Path_286-2" data-name="Path 286" d="M0,.23v11" transform="translate(15 79.5)" fill="none" stroke={expDateStroke} strokeWidth="3"/>
                        <path id="Path_286-3" data-name="Path 286" d="M0,.23v11" transform="translate(15 104.5)" fill="none" stroke={oppNumStroke} strokeWidth="3"/>
                        <path id="Path_286-4" data-name="Path 286" d="M0,.23v11" transform="translate(15 129.5)" fill="none" stroke={estTotValStroke} strokeWidth="3"/>
                        <path id="Path_286-5" data-name="Path 286" d="M0,.23v11" transform="translate(15 154.5)" fill="none" stroke={quoteNumStroke} strokeWidth="3"/>
                        <path id="Path_286-5" data-name="Path 286" d="M0,.23v11" transform="translate(15 179.5)" fill="none" stroke={subjectStroke} strokeWidth="3"/>
                        <path id="Path_288" data-name="Path 288" d="M0,.23V11.484" transform="translate(15 204.516)" fill="none" stroke={productInfoStroke} strokeWidth="3"/>
                        <path id="Path_352" data-name="Path 352" d="M0,.23V11.484" transform="translate(15 244.643)" fill="none" stroke={machinesStroke} strokeWidth="3"/>
                        <g id="Path_283" data-name="Path 283" fill="none">
                            <path d="M 15 28.5 C 11.39402008056641 28.5 8.003870010375977 27.09575080871582 5.454060077667236 24.54594039916992 C 2.904249906539917 21.99612998962402 1.5 18.60597991943359 1.5 15 C 1.5 11.39402008056641 2.904249906539917 8.003870010375977 5.454060077667236 5.454060077667236 C 8.003870010375977 2.904249906539917 11.39402008056641 1.5 15 1.5 C 18.60597991943359 1.5 21.99612998962402 2.904249906539917 24.54594039916992 5.454060077667236 C 27.09575080871582 8.003870010375977 28.5 11.39402008056641 28.5 15 C 28.5 18.60597991943359 27.09575080871582 21.99612998962402 24.54594039916992 24.54594039916992 C 21.99612998962402 27.09575080871582 18.60597991943359 28.5 15 28.5 Z" stroke="none"/>
                            <path d="M 15 3 C 11.79467964172363 3 8.781219482421875 4.248220443725586 6.514720916748047 6.514720916748047 C 4.248220443725586 8.781219482421875 3 11.79467964172363 3 15 C 3 18.20532035827637 4.248220443725586 21.21878051757812 6.514720916748047 23.48527908325195 C 8.781219482421875 25.75177955627441 11.79467964172363 27 15 27 C 18.20532035827637 27 21.21878051757812 25.75177955627441 23.48527908325195 23.48527908325195 C 25.75177955627441 21.21878051757812 27 18.20532035827637 27 15 C 27 11.79467964172363 25.75177955627441 8.781219482421875 23.48527908325195 6.514720916748047 C 21.21878051757812 4.248220443725586 18.20532035827637 3 15 3 M 15 0 C 23.28426933288574 0 30 6.715730667114258 30 15 C 30 23.28426933288574 23.28426933288574 30 15 30 C 6.715730667114258 30 0 23.28426933288574 0 15 C 0 6.715730667114258 6.715730667114258 0 15 0 Z" stroke="none" fill="#17a517"/>
                        </g>
                        <g id="Path_284" data-name="Path 284" transform="translate(0.5 215.5)" fill="none">
                            <path d="M 15 28.5 C 11.39402008056641 28.5 8.003870010375977 27.09575080871582 5.454060077667236 24.54594039916992 C 2.904249906539917 21.99612998962402 1.5 18.60597991943359 1.5 15 C 1.5 11.39402008056641 2.904249906539917 8.003870010375977 5.454060077667236 5.454060077667236 C 8.003870010375977 2.904249906539917 11.39402008056641 1.5 15 1.5 C 18.60597991943359 1.5 21.99612998962402 2.904249906539917 24.54594039916992 5.454060077667236 C 27.09575080871582 8.003870010375977 28.5 11.39402008056641 28.5 15 C 28.5 18.60597991943359 27.09575080871582 21.99612998962402 24.54594039916992 24.54594039916992 C 21.99612998962402 27.09575080871582 18.60597991943359 28.5 15 28.5 Z" stroke="none"/>
                            <path d="M 15 3 C 11.79467964172363 3 8.781219482421875 4.248220443725586 6.514720916748047 6.514720916748047 C 4.248220443725586 8.781219482421875 3 11.79467964172363 3 15 C 3 18.20532035827637 4.248220443725586 21.21878051757812 6.514720916748047 23.48527908325195 C 8.781219482421875 25.75177955627441 11.79467964172363 27 15 27 C 18.20532035827637 27 21.21878051757812 25.75177955627441 23.48527908325195 23.48527908325195 C 25.75177955627441 21.21878051757812 27 18.20532035827637 27 15 C 27 11.79467964172363 25.75177955627441 8.781219482421875 23.48527908325195 6.514720916748047 C 21.21878051757812 4.248220443725586 18.20532035827637 3 15 3 M 15 0 C 23.28426933288574 0 30 6.715730667114258 30 15 C 30 23.28426933288574 23.28426933288574 30 15 30 C 6.715730667114258 30 0 23.28426933288574 0 15 C 0 6.715730667114258 6.715730667114258 0 15 0 Z" stroke="none" fill={productInfoStroke}/>
                        </g>
                        <text id="_2" data-name="2" transform="translate(10.453 237)" fill={productInfoStroke} fontSize="18" fontFamily="SegoeUI-Semibold, Segoe UI" fontWeight="600"><tspan x="0" y="0">2</tspan></text>
                        <text id="_1" data-name="1" transform="translate(11 22.5)" fill="#17a517" fontSize="20" fontFamily="SegoeUI-Semibold, Segoe UI" fontWeight="600"><tspan x="0" y="0">1</tspan></text>
                        <text id="General_Information" data-name="General Information" transform="translate(40 20)" fontSize="15" fontFamily="SegoeUI-Semibold, Segoe UI" fontWeight="600"><tspan x="0" y="0">General Information</tspan></text>
                        <text id="Product_Information" data-name="Product Information" transform="translate(40 237)" fill={productInfoFill} fontSize="15" fontFamily="SegoeUI-Semibold, Segoe UI" fontWeight="600"><tspan x="0" y="0">Product Information</tspan></text>
                        <foreignObject x="40" y="39" width="100%" height="16" className="progress_foreign_obj">
                            <p className="progress_bar_text" style={{ fontFamily: normFont, fontSize: "12px" }}>
                                <span
                                    style={{
                                        color: black,
                                        fontFamily: boldFont,
                                        fontWeight: fSize
                                    }}
                                >
                                    Customer{customerInformation.customer && ': '}</span>
                                {customerInformation.customer}
                            </p>
                        </foreignObject>
                        <foreignObject x="40" y="64" width="100%" height="16" className="progress_foreign_obj">
                            <p className="progress_bar_text" style={{ fontFamily: normFont, fontSize: "12px" }}>
                                <span
                                    style={{
                                        color: actDateFontFill,
                                        fontFamily: actDateFontFam,
                                        fontWeight: actDateFontWeight
                                    }}
                                >
                                    Activation Date{activationDuration.activationDate && ': '}</span>
                                {activationDuration.activationDate}
                            </p>
                        </foreignObject>
                        <foreignObject x="40" y="89" width="100%" height="16" className="progress_foreign_obj">
                            <p className="progress_bar_text" style={{ fontFamily: normFont, fontSize: "12px" }}>
                                <span
                                    style={{
                                        color: expDateFontFill,
                                        fontFamily: expDateFontFam,
                                        fontWeight: expDateFontWeight
                                    }}
                                >
                                    Expiration Date{activationDuration.expirationDate && ': '}</span>
                                {activationDuration.expirationDate}
                            </p>
                        </foreignObject>
                        <foreignObject x="40" y="114" width="100%" height="16" className="progress_foreign_obj">
                            <p className="progress_bar_text" style={{ fontFamily: normFont, fontSize: "12px" }}>
                                <span
                                    style={{
                                        color: oppNumFontFill,
                                        fontFamily: oppNumFontFam,
                                        fontWeight: oppNumFontWeight
                                    }}
                                >
                                    Opportunity No.{generalDetails.opportunityNumber && ': '}</span>
                                {generalDetails.opportunityNumber}
                            </p>
                        </foreignObject>
                        <foreignObject x="40" y="139" width="100%" height="16" className="progress_foreign_obj">
                            <p className="progress_bar_text" style={{ fontFamily: normFont, fontSize: "12px" }}>
                                <span
                                    style={{
                                        color: estTotValFontFill,
                                        fontFamily: estTotValFontFam,
                                        fontWeight: estTotValFontWeight
                                    }}
                                >
                                    Est. Total Value{generalDetails.estimatedTotalValue && ': '}</span>
                                {generalDetails.estimatedTotalValue}
                            </p>
                        </foreignObject>
                        <foreignObject x="40" y="164" width="100%" height="16" className="progress_foreign_obj">
                            <p className="progress_bar_text" style={{ fontFamily: normFont, fontSize: "12px" }}>
                                <span
                                    style={{
                                        color: quoteNumFontFill,
                                        fontFamily: quoteNumFontFam,
                                        fontWeight: quoteNumFontWeight
                                    }}
                                >
                                    Quote No.{quoteNumber && ': '}</span>
                                {quoteNumber}
                            </p>
                        </foreignObject>
                        <foreignObject x="40" y="189" width="100%" height="16" className="progress_foreign_obj">
                            <p className="progress_bar_text" style={{ fontFamily: normFont, fontSize: "12px" }}>
                                <span
                                    style={{
                                        color: subjectFontFill,
                                        fontFamily: subjectFontFam,
                                        fontWeight: subjectFontWeight
                                    }}
                                >
                                    Subject{generalDetails.quoteSubject && ': '}</span>
                                {generalDetails.quoteSubject}
                            </p>
                        </foreignObject>
                        <g id="customerEllipse" data-name="Ellipse 12" transform="translate(7.5 40)" fill={customerFill} stroke={green} strokeWidth="3">
                            <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                            <circle cx="7.5" cy="7.5" r="6" fill="none"/>
                        </g>
                        <g id="activationDateEllipse" data-name="Ellipse 14" transform="translate(7.5 65)" fill={actDateFill} stroke={actDateStroke} strokeWidth="3">
                            <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                            <circle cx="7.5" cy="7.5" r="6" fill="none"/>
                        </g>
                        <g id="expirationDateEllipse" transform="translate(7.5 90)" fill={expDateFill} stroke={expDateStroke} strokeWidth="3">
                            <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                            <circle cx="7.5" cy="7.5" r="6" fill="none"/>
                        </g>
                        <g id="opportunityNumberEllipse" transform="translate(7.5 115)" fill={oppNumFill} stroke={oppNumStroke} strokeWidth="3">
                            <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                            <circle cx="7.5" cy="7.5" r="6" fill="none"/>
                        </g>
                        <g id="estimatedTotalValueEllipse" transform="translate(7.5 140)" fill={estTotValFill} stroke={estTotValStroke} strokeWidth="3">
                            <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                            <circle cx="7.5" cy="7.5" r="6" fill="none"/>
                        </g>
                        <g id="quoteNumberEllipse" transform="translate(7.5 165)" fill={quoteNumFill} stroke={quoteNumStroke} strokeWidth="3">
                            <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                            <circle cx="7.5" cy="7.5" r="6" fill="none"/>
                        </g>
                        <g id="subjectEllipse" transform="translate(7.5 190)" fill={subjectFill} stroke={subjectStroke} strokeWidth="3">
                            <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                            <circle cx="7.5" cy="7.5" r="6" fill="none"/>
                        </g>
                    </svg>
                    {
                        productEllipses.length > 0 ? productEllipses : <LineDown stroke="#939393" />
                    }
                    <svg xmlns="http://www.w3.org/2000/svg" width="188" height="30" viewBox="0 0 188 30">
                        <g id="Ellipse_5" data-name="Ellipse 5" fill="none" stroke={machinesStroke} strokeWidth="3">
                            <circle cx="15" cy="15" r="15" stroke="none"/>
                            <circle cx="15" cy="15" r="13.5" fill="none"/>
                        </g>
                        <text id="_3" data-name="3" transform="translate(10.453 22)" fill={machinesStroke} fontSize="18" fontFamily="SegoeUI-Semibold, Segoe UI" fontWeight="600"><tspan x="0" y="0">3</tspan></text>
                        <text id="Machines_and_Tools" data-name="Machines and Tools" transform="translate(39.5 21.801)" fill={machinesFill} fontSize="15" fontFamily="SegoeUI-Semibold, Segoe UI" fontWeight="600"><tspan x="0" y="0">Machines and Tools</tspan></text>
                    </svg>
                    <LineDown stroke={productEllipses.length > 0 ? green : gray} />
                    {
                        machineEllipses.length > 0 ? machineEllipses : <LineDown stroke={productEllipses.length > 0 ? green : gray} />
                    }
                    <svg xmlns="http://www.w3.org/2000/svg" width="188" height="30" viewBox="0 0 188 30">
                        <defs>
                            <clipPath id="clipPath">
                                <rect id="Rectangle_145" data-name="Rectangle 145" width="24" height="24" fill="none" stroke="#939393" strokeWidth="1"/>
                            </clipPath>
                        </defs>
                        <g id="Group_112" data-name="Group 112">
                            <g id="Path_285" data-name="Path 285" fill="none">
                                <path d="M 15 28.5 C 11.39402008056641 28.5 8.003870010375977 27.09575080871582 5.454060077667236 24.54594039916992 C 2.904249906539917 21.99612998962402 1.5 18.60597991943359 1.5 15 C 1.5 11.39402008056641 2.904249906539917 8.003870010375977 5.454060077667236 5.454060077667236 C 8.003870010375977 2.904249906539917 11.39402008056641 1.5 15 1.5 C 18.60597991943359 1.5 21.99612998962402 2.904249906539917 24.54594039916992 5.454060077667236 C 27.09575080871582 8.003870010375977 28.5 11.39402008056641 28.5 15 C 28.5 18.60597991943359 27.09575080871582 21.99612998962402 24.54594039916992 24.54594039916992 C 21.99612998962402 27.09575080871582 18.60597991943359 28.5 15 28.5 Z" stroke="none"/>
                                <path d="M 15 3 C 11.79467964172363 3 8.781219482421875 4.248220443725586 6.514720916748047 6.514720916748047 C 4.248220443725586 8.781219482421875 3 11.79467964172363 3 15 C 3 18.20532035827637 4.248220443725586 21.21878051757812 6.514720916748047 23.48527908325195 C 8.781219482421875 25.75177955627441 11.79467964172363 27 15 27 C 18.20532035827637 27 21.21878051757812 25.75177955627441 23.48527908325195 23.48527908325195 C 25.75177955627441 21.21878051757812 27 18.20532035827637 27 15 C 27 11.79467964172363 25.75177955627441 8.781219482421875 23.48527908325195 6.514720916748047 C 21.21878051757812 4.248220443725586 18.20532035827637 3 15 3 M 15 0 C 23.28426933288574 0 30 6.715730667114258 30 15 C 30 23.28426933288574 23.28426933288574 30 15 30 C 6.715730667114258 30 0 23.28426933288574 0 15 C 0 6.715730667114258 6.715730667114258 0 15 0 Z" stroke="none" fill={reviewStroke}/>
                            </g>
                        </g>
                        <g id="Mask_Group_75" data-name="Mask Group 75" transform="translate(3 3)" clipPath="url(#clipPath)">
                            <g id="checkmark-done-sharp" transform="translate(4.706 6.996)">
                                <path id="Path_164" data-name="Path 164" d="M18.469,6.877l-3.82,4.617L11.1,15.789,8.068,12.6" transform="translate(-4.837 -4.911)" fill="none" stroke={reviewStroke}  strokeLinecap="square" strokeMiterlimit="10" strokeWidth="2.383"/>
                                <path id="Path_165" data-name="Path 165" d="M5.889,19.379,2.545,15.812" transform="translate(-2.545 -7.395)" fill="none" stroke={reviewStroke} strokeLinecap="square" strokeMiterlimit="10" strokeWidth="2.383"/>
                                <path id="Path_166" data-name="Path 166" d="M18.037,6.877,16.065,9.286,12.78,13.3" transform="translate(-6.678 -6.877)" fill="none" stroke={reviewStroke} strokeLinecap="square" strokeMiterlimit="10" strokeWidth="2.383"/>
                            </g>
                        </g>
                        <text id="Quote_Review" data-name="Quote Review" transform="translate(39 21)" fill={reviewFill} fontSize="15" fontFamily="SegoeUI-Semibold, Segoe UI" fontWeight="600"><tspan x="0" y="0">Quote Review</tspan></text>
                    </svg>
                </>
            }
            {
                sidePanelTitle === 'Messages' &&
                <RevisionMessages
                    activeQuote={ completeQuote }
                    suggestions={ completeQuote.quote[0].suggestions }
                    modifications={ completeQuote.quote[0].modifications }
                    flags={ completeQuote.quote[0].flags }
                />
            }
            {
                sidePanelTitle === 'Account' && 
                <AccountInfoSP />
            }
            {
                loading !== '' && loading !== 'loading' &&
                <div className="create_quote_confirmation_container">
                    <p className="create_quote_confirmation">{ confirmationMessage(loading, isQuoteRevision) }</p>
                </div>
            }
        </div>
    );
}

const LineDown = props =>{
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="188" height="11.254" viewBox="0 0 188 11.254">
            <path id="Path_353" data-name="Path 353" d="M0,.23V11.484" transform="translate(15 -0.23)" fill="none" stroke={props.stroke} strokeWidth="3"/>
        </svg>
    );
};

const EllipseAndLine = props =>{
    const { lineStroke, value, ellipseFill } = props;
    const labelFill = value ? "#000000" : "#939393";
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="188" height="25.5" viewBox="0 0 188 25.5">
            <path id="Path_356" data-name="Path 356" d="M0,.23V11.484" transform="translate(15 14)" fill="none" stroke={ lineStroke } strokeWidth="3"/>
            <foreignObject x="40" y="0" width="100%" height="16" className="progress_foreign_obj">
                <p className="progress_bar_text" style={{ fontFamily: "SegoeUI, Segoe UI", fontSize: "12px" }}>
                    <span
                        style={{
                            color: labelFill,
                            fontFamily: "SegoeUI-semibold, Segoe UI",
                            fontWeight: "600"
                        }}
                    >
                        {props.label}{props.value && ': '}</span>
                    {props.value}
                </p>
            </foreignObject>
            <g id="Ellipse_32" data-name="Ellipse 32" transform="translate(7.5 0)" fill={ellipseFill} stroke="#17a517" strokeWidth="3">
                <circle cx="7.5" cy="7.5" r="7.5" stroke="none"/>
                <circle cx="7.5" cy="7.5" r="6" fill="none"/>
            </g>
        </svg>
    );
};

export default CreateQuote;
export { CreateQuoteSPC };